import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/utils/cn';
import { getRoleTheme, type BackofficeUserType } from '@/backoffice/config/theme';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  showHome?: boolean;
  className?: string;
  userType?: BackofficeUserType;
}

const routeLabels: Record<string, string> = {
  client: 'Espace Client',
  livreur: 'Livreur communautaire',
  'livreur-pro': 'Livreur professionnel',
  admin: 'Administration',
  'super-admin': 'Super Administration',
  commandes: 'Commandes',
  suivi: 'Suivi en direct',
  adresses: 'Mes adresses',
  voisins: 'Voisins disponibles',
  portefeuille: 'Portefeuille',
  historique: 'Historique',
  missions: 'Missions',
  itineraire: 'Feuille de route',
  revenus: 'Revenus',
  zones: 'Mes zones',
  documents: 'Documents',
  assurance: 'Assurance',
  trajets: 'Mes trajets',
  gains: 'Gains',
  infos: 'Mes informations',
  utilisateurs: 'Utilisateurs',
  litiges: 'Litiges',
  infrastructures: 'Infrastructures',
  promotions: 'Promotions',
  rapports: 'Rapports',
  administrateurs: 'Administrateurs',
  configuration: 'Configuration',
  tarification: 'Tarification',
  nouvelle: 'Nouvelle commande',
};

export function Breadcrumbs({
  items,
  showHome = true,
  className,
  userType = 'client',
}: BreadcrumbsProps) {
  const location = useLocation();
  const theme = getRoleTheme(userType);
  const segments = location.pathname.split('/').filter(Boolean);

  const generatedItems: BreadcrumbItem[] = [];
  if (segments[0] === 'dashboard') {
    for (let i = 1; i < segments.length; i++) {
      const segment = segments[i];
      const path = `/dashboard/${segments.slice(1, i + 1).join('/')}`;
      const label =
        routeLabels[segment] ||
        segment.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

      generatedItems.push({
        label,
        path: i === segments.length - 1 ? undefined : path,
      });
    }
  }

  const displayItems = items.length > 0 ? items : generatedItems;
  const homePath =
    segments[0] === 'dashboard' && segments[1]
      ? `/dashboard/${segments[1]}`
      : '/';

  if (displayItems.length === 0) return null;

  return (
    <nav className={cn('flex items-center gap-1 text-sm', className)} aria-label="Breadcrumb">
      {showHome && (
        <>
          <Link
            to={homePath}
            className={cn('p-1 rounded hover:bg-gray-100 transition-colors', theme.link, theme.linkHover)}
          >
            <Home className="w-4 h-4" />
          </Link>
          {displayItems.length > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
        </>
      )}

      {displayItems.map((item, index) => (
        <div key={index} className="flex items-center gap-1">
          {index > 0 && <ChevronRight className="w-4 h-4 text-gray-400" />}
          {item.path ? (
            <Link to={item.path} className="text-gray-600 hover:text-gray-900 transition-colors">
              {item.label}
            </Link>
          ) : (
            <span className="text-gray-900 font-medium">{item.label}</span>
          )}
        </div>
      ))}
    </nav>
  );
}

export function getCurrentTab(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  return segments.length > 2 ? segments[segments.length - 1] : null;
}
